//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { TaskContext } from '../TaskContext/TaskContext';
import { Fields     as Field } from './components/Fields';
import { Thumbnails as Thumb } from './components/Thumbnails';
import { Image      as Image } from './components/Image';
import { ProgressBar } from './components/ProgressBar/ProgressBar';
import { GoToPage } from './components/GoToPage/GoToPage';

// === HOOKS ===
import { useTaskUpdate } from 'components/useTaskUpdate';
import { useTaskCancel } from 'components/useTaskCancel';

// === API ===
import { emit, onTask, removeListener, requestCurrentFieldFocus, retainTask, returnFieldFocus, returnTask } from 'api/prokey/prokey.websocket';
//#endregion

//-----------------------------------------------------------------
export function TaskPanel() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const {
    task, setTask,
  } = useContext( TaskContext );
  const theme       = useTheme();
  const updates     = useTaskUpdate();
  const cancel_data = useTaskCancel();
  const has_task = !!task.id;

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display : 'grid' ,
      padding : '1rem' ,
      width   : `100%` ,
      height  : `calc( 100% - 1rem )`,
      zIndex  : '0'    ,
      gridTemplateColumns : 'repeat(12, 1fr)',
      gridGap             : '1rem',
      backgroundColor : theme.palette.background.secondary[theme.palette.mode],
    },
    dashboard: {

    },
  }

  const 
    CONTAINER = {
      sx: _styles.container,
    },
    FIELD = {
      folder    : task.folder    ,
      documents : task.documents ,
    },
    IMAGE = {
      items : task.images,
    },
    THUMB = {
      documents : task.documents ,
      images    : task.images    ,
    }

  //#endregion

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []); // empty dependency = called only once at startup

  useEffect( () => {

    //-----------------------------------------------------------------
    function handleTask( task ) {
      //-----------------------------------------------------------------
      setTask(task);
    };

    onTask( handleTask );
    
    return () => {
      if( task ) retainTask();
      removeListener( 'TASK' , handleTask );
    };
  }, [] );

  useEffect( () => {
    if( !updates ) return;

    const temp = { ...task };

    // === DOCUMENTS
    if( updates.documents?.order ) temp.documents.order = updates.documents.order;

    updates.documents?.create?.forEach( document => {
      temp.documents.items[ document.Id ] = document;
    });
    updates.documents?.update?.forEach( document => {
      document.timestamp = Date.now();
      temp.documents.items[ document.Id ] = document;
    });
    updates.documents?.remove?.forEach( id => {      
      delete temp.documents.items[ id ];
    });
    
    // === IMAGES
    if( updates.images?.order ) temp.images.order = updates.images.order;
    
    updates.images?.update?.forEach( image => {
      emit( 'IMAGE-'+ image.id, image );
    });

    setTask( temp );

  }, [updates])

  useEffect(() => {
    if( !task || task.id !== cancel_data.id ) return; 

    returnTask();
  }, [cancel_data]);

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if (event.keyCode === 192) { // ` - grave accent key
      event.preventDefault();
      requestCurrentFieldFocus();
    }
  } 

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( !has_task ) return null;
    
    return (
      <>
        <Box { ...CONTAINER } >
          <Field { ...FIELD } />
          <Image { ...IMAGE } />
          <Thumb { ...THUMB } />
        </Box>
        <ProgressBar />
      </>
    )
  }
  //#endregion

  return display();
}